import React, { useEffect, useMemo, useState } from 'react';
import axios from '../api/axiosConfig';
import Sidebar from '../components/Sidebar';
import Navbar from '../components/Navbar';
import Pagination from '../components/Pagination';
import DetalleTramiteModal from '../components/DetalleTramiteModal';
import CuplLiberadosPanel from '../components/CuplLiberadosPanel';
import * as XLSX from 'xlsx';
import './Profiles.css';

// Submódulo "Cambios Trámites": lista las ediciones en las que se cambió el tipo de trámite.
const filterInputStyle = {
  padding: '10px 14px',
  border: '1px solid var(--input-border)',
  borderRadius: '10px',
  background: 'var(--input-bg)',
  color: 'var(--text-primary)',
  fontSize: '13px',
  fontWeight: '500',
  outline: 'none',
  width: '100%'
};
const labelStyle = { fontSize: '11px', fontWeight: '800', color: 'var(--text-secondary)', textTransform: 'uppercase' };

const formatFecha = (d) => d ? new Date(d).toLocaleString('es-CO', { timeZone: 'America/Bogota', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—';

const CambiosTramites = () => {
  const [registros, setRegistros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [filtroTransito, setFiltroTransito] = useState('todos');
  const [filtroConcesionario, setFiltroConcesionario] = useState('todos');
  const [fechaInicio, setFechaInicio] = useState('');
  const [fechaFin, setFechaFin] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);

  const [selectedTramiteId, setSelectedTramiteId] = useState(null);
  const [filtroRevision, setFiltroRevision] = useState('todos');
  const [hallazgoModal, setHallazgoModal] = useState(null); // { registro, nota }
  const [savingId, setSavingId] = useState(null);

  const actualizarRegistro = (id, cambios) =>
    setRegistros(prev => prev.map(r => (r.id === id ? { ...r, ...cambios } : r)));

  const toggleRevisado = async (r) => {
    setSavingId(r.id);
    try {
      const res = await axios.put(`/api/tramites/cambios-tipo/${r.id}/revisado`, { revisado: !r.revisado });
      actualizarRegistro(r.id, { revisado: res.data.revisado, revisado_por: res.data.revisado_por, revisado_at: res.data.revisado_at });
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Error al guardar la revisión.');
    } finally {
      setSavingId(null);
    }
  };

  const guardarHallazgo = async (hallazgo) => {
    const r = hallazgoModal.registro;
    setSavingId(r.id);
    try {
      const res = await axios.put(`/api/tramites/cambios-tipo/${r.id}/hallazgo`, { hallazgo, nota: hallazgoModal.nota });
      actualizarRegistro(r.id, {
        hallazgo: res.data.hallazgo, hallazgo_nota: res.data.hallazgo_nota,
        hallazgo_por: res.data.hallazgo_por, hallazgo_at: res.data.hallazgo_at
      });
      setHallazgoModal(null);
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Error al guardar el hallazgo.');
    } finally {
      setSavingId(null);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const params = new URLSearchParams();
        if (fechaInicio) params.append('fecha_inicio', fechaInicio);
        if (fechaFin) params.append('fecha_fin', fechaFin);
        const res = await axios.get(`/api/tramites/cambios-tipo?${params.toString()}`);
        setRegistros(Array.isArray(res.data) ? res.data : []);
        setError('');
      } catch (err) {
        console.error(err);
        setError('Error al cargar los cambios de tipo de trámite.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [fechaInicio, fechaFin]);

  // Opciones de filtro tomadas de los propios registros (respeta lo que cada perfil puede ver)
  const transitoOptions = useMemo(() => {
    const map = new Map();
    registros.forEach(r => { if (r.transito_id) map.set(String(r.transito_id), r.transito_name || `Tránsito #${r.transito_id}`); });
    return [...map.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [registros]);

  const concesionarioOptions = useMemo(() => {
    const map = new Map();
    registros.forEach(r => map.set(String(r.concesionario_id || 'ninguno'), r.concesionario_name || 'Independiente'));
    return [...map.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [registros]);

  const filtered = useMemo(() => registros.filter(r => {
    if (filtroTransito !== 'todos' && String(r.transito_id) !== filtroTransito) return false;
    if (filtroConcesionario !== 'todos' && String(r.concesionario_id || 'ninguno') !== filtroConcesionario) return false;
    if (filtroRevision === 'pendientes' && r.revisado) return false;
    if (filtroRevision === 'revisados' && !r.revisado) return false;
    if (filtroRevision === 'hallazgos' && !r.hallazgo) return false;
    if (searchTerm.trim()) {
      const term = searchTerm.trim().toLowerCase();
      const hay = [r.tramite_id, r.placa, r.propietario, r.usuario_cambio, r.tipo_anterior, r.tipo_nuevo]
        .some(v => String(v || '').toLowerCase().includes(term));
      if (!hay) return false;
    }
    return true;
  }), [registros, filtroTransito, filtroConcesionario, filtroRevision, searchTerm]);

  const resumen = useMemo(() => ({
    total: registros.length,
    revisados: registros.filter(r => r.revisado).length,
    hallazgos: registros.filter(r => r.hallazgo).length
  }), [registros]);

  const indexOfLast = currentPage * itemsPerPage;
  const currentItems = filtered.slice(indexOfLast - itemsPerPage, indexOfLast);

  const exportToExcel = () => {
    if (filtered.length === 0) {
      alert('No hay datos para exportar.');
      return;
    }
    const data = filtered.map(r => ({
      'Fecha del Cambio': formatFecha(r.fecha_cambio),
      'Trámite': r.tramite_id,
      'Placa': r.placa || '—',
      'Propietario': r.propietario || '—',
      'Tránsito': r.transito_name || '—',
      'Concesionario': r.concesionario_name || '—',
      'Tipo Anterior': r.tipo_anterior || '—',
      'Tipo Nuevo': r.tipo_nuevo || '—',
      'Cambiado por': r.usuario_cambio || '—',
      'Creador del Trámite': r.creador_name || '—',
      'Estado Actual': r.estado || '—',
      'Revisado y Subsanado': r.revisado ? 'Sí' : 'No',
      'Revisado por': r.revisado_por || '—',
      'Fecha Revisión': formatFecha(r.revisado_at),
      'Hallazgo': r.hallazgo ? 'Sí' : 'No',
      'Detalle del Hallazgo': r.hallazgo_nota || '—',
      'Hallazgo registrado por': r.hallazgo_por || '—'
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Cambios Trámites');
    XLSX.writeFile(wb, `Cambios_Tramites_${new Date().toLocaleDateString('es-CO').replace(/\//g, '-')}.xlsx`);
  };

  const resetPage = (fn) => (e) => { fn(e.target.value); setCurrentPage(1); };

  return (
    <div className="app-layout">
      <Sidebar />
      <div className="main-content">
        <Navbar title="🔄 Cambios Trámites" />

        <div className="page-content">
          {error && <div className="error-banner">{error}</div>}

          <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
            <div className="header-title">
              <h2>Cambios de Tipo de Trámite</h2>
              <p className="subtitle">
                Registros en los que se cambió el tipo de trámite seleccionado ({filtered.length}) ·
                ✅ {resumen.revisados} de {resumen.total} revisados · 🚩 {resumen.hallazgos} hallazgo(s)
              </p>
            </div>
            <button
              onClick={exportToExcel}
              style={{ padding: '10px 16px', background: '#10b981', color: 'white', border: 'none', borderRadius: '10px', fontWeight: '600', cursor: 'pointer' }}
            >
              📥 Exportar a Excel
            </button>
          </div>

          <CuplLiberadosPanel />

          <div className="dashboard-filters" style={{
            background: 'var(--card-bg)', padding: '16px 24px', borderRadius: '14px', marginBottom: '20px',
            boxShadow: 'var(--card-shadow)', border: '1px solid var(--card-border)',
            display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '14px'
          }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label htmlFor="f-transito" style={labelStyle}>Tránsito</label>
              <select id="f-transito" value={filtroTransito} onChange={resetPage(setFiltroTransito)} style={filterInputStyle}>
                <option value="todos">Todos</option>
                {transitoOptions.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
              </select>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label htmlFor="f-concesionario" style={labelStyle}>Concesionario</label>
              <select id="f-concesionario" value={filtroConcesionario} onChange={resetPage(setFiltroConcesionario)} style={filterInputStyle}>
                <option value="todos">Todos</option>
                {concesionarioOptions.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
              </select>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label htmlFor="f-revision" style={labelStyle}>Revisión</label>
              <select id="f-revision" value={filtroRevision} onChange={resetPage(setFiltroRevision)} style={filterInputStyle}>
                <option value="todos">Todos</option>
                <option value="pendientes">Pendientes por revisar</option>
                <option value="revisados">Revisados y subsanados</option>
                <option value="hallazgos">Con hallazgo</option>
              </select>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label htmlFor="f-desde" style={labelStyle}>Desde</label>
              <input id="f-desde" type="date" value={fechaInicio} onChange={resetPage(setFechaInicio)} style={filterInputStyle} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label htmlFor="f-hasta" style={labelStyle}>Hasta</label>
              <input id="f-hasta" type="date" value={fechaFin} onChange={resetPage(setFechaFin)} style={filterInputStyle} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label htmlFor="f-buscar" style={labelStyle}>Buscar</label>
              <input id="f-buscar" type="text" placeholder="Trámite, placa, propietario..." value={searchTerm} onChange={resetPage(setSearchTerm)} style={filterInputStyle} />
            </div>
          </div>

          {loading ? (
            <p className="loading">Cargando cambios de tipo de trámite...</p>
          ) : (
            <div className="table-wrapper">
              <div className="table-responsive">
                <table className="users-table">
                  <thead>
                    <tr>
                      <th>Fecha del Cambio</th>
                      <th>Trámite</th>
                      <th>Placa</th>
                      <th>Propietario</th>
                      <th>Tránsito</th>
                      <th>Concesionario</th>
                      <th>Tipo Anterior</th>
                      <th>Tipo Nuevo</th>
                      <th>Cambiado por</th>
                      <th>Revisión</th>
                      <th>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentItems.map(r => (
                      <tr key={r.id} style={r.hallazgo ? { background: '#fef2f2' } : r.revisado ? { background: '#f0fdf4' } : undefined}>
                        <td data-label="Fecha">{formatFecha(r.fecha_cambio)}</td>
                        <td data-label="Trámite"><strong>#{r.tramite_id}</strong></td>
                        <td data-label="Placa">{r.placa ? <span className="plate-badge-sm">{r.placa}</span> : '—'}</td>
                        <td data-label="Propietario">{r.propietario || '—'}</td>
                        <td data-label="Tránsito">{r.transito_name || '—'}</td>
                        <td data-label="Concesionario">{r.concesionario_name || '—'}</td>
                        <td data-label="Tipo Anterior" style={{ color: '#b91c1c', fontWeight: 600 }}>{r.tipo_anterior || '—'}</td>
                        <td data-label="Tipo Nuevo" style={{ color: '#15803d', fontWeight: 600 }}>{r.tipo_nuevo || '—'}</td>
                        <td data-label="Cambiado por">{r.usuario_cambio || '—'}</td>
                        <td data-label="Revisión" style={{ fontSize: '11px', minWidth: '140px' }}>
                          {r.revisado
                            ? <div style={{ color: '#15803d', fontWeight: 700 }}>✅ Revisado<br /><span style={{ fontWeight: 500, color: '#64748b' }}>{r.revisado_por} · {formatFecha(r.revisado_at)}</span></div>
                            : <span style={{ color: '#94a3b8' }}>Pendiente</span>}
                          {r.hallazgo && (
                            <div style={{ marginTop: '4px', color: '#b91c1c', fontWeight: 700 }} title={r.hallazgo_nota}>
                              🚩 {r.hallazgo_nota}
                            </div>
                          )}
                        </td>
                        <td data-label="Acciones">
                          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                            <button
                              type="button"
                              onClick={() => toggleRevisado(r)}
                              disabled={savingId === r.id}
                              aria-pressed={r.revisado}
                              title={r.revisado ? 'Quitar la marca de revisado' : 'Marcar como revisado y subsanado'}
                              style={{ padding: '6px 12px', background: r.revisado ? '#15803d' : '#e2e8f0', color: r.revisado ? 'white' : '#334155', border: 'none', borderRadius: '8px', fontWeight: '700', cursor: 'pointer', fontSize: '12px' }}
                            >
                              {r.revisado ? '✅ Revisado' : '☐ Revisar'}
                            </button>
                            <button
                              type="button"
                              onClick={() => setHallazgoModal({ registro: r, nota: r.hallazgo_nota || '' })}
                              disabled={savingId === r.id}
                              title={r.hallazgo ? 'Ver o editar el hallazgo' : 'Registrar un hallazgo'}
                              style={{ padding: '6px 12px', background: r.hallazgo ? '#dc2626' : '#fee2e2', color: r.hallazgo ? 'white' : '#b91c1c', border: 'none', borderRadius: '8px', fontWeight: '700', cursor: 'pointer', fontSize: '12px' }}
                            >
                              🚩 Hallazgo
                            </button>
                            <button
                              type="button"
                              onClick={() => setSelectedTramiteId(r.tramite_id)}
                              style={{ padding: '6px 12px', background: '#2563eb', color: 'white', border: 'none', borderRadius: '8px', fontWeight: '600', cursor: 'pointer', fontSize: '12px' }}
                            >
                              👁️ Ver Detalle
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {filtered.length === 0 && (
                <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
                  No hay cambios de tipo de trámite con los filtros seleccionados.
                </div>
              )}
              <Pagination
                currentPage={currentPage}
                totalItems={filtered.length}
                itemsPerPage={itemsPerPage}
                onPageChange={setCurrentPage}
                onItemsPerPageChange={(n) => { setItemsPerPage(n); setCurrentPage(1); }}
              />
            </div>
          )}
        </div>
      </div>

      {hallazgoModal && (
        <div className="modal-overlay" role="dialog" aria-modal="true" aria-labelledby="hallazgo-title">
          <div className="modal-content" style={{ maxWidth: '520px', width: '95%' }}>
            <h3 id="hallazgo-title" style={{ marginTop: 0 }}>🚩 Hallazgo — Trámite #{hallazgoModal.registro.tramite_id}</h3>
            <p style={{ fontSize: '13px', color: '#475569' }}>
              Placa {hallazgoModal.registro.placa || '—'} · {hallazgoModal.registro.tipo_anterior} → {hallazgoModal.registro.tipo_nuevo}
            </p>
            <label htmlFor="hallazgo-nota" style={labelStyle}>Describa el hallazgo</label>
            <textarea
              id="hallazgo-nota"
              rows={4}
              autoFocus
              placeholder="Ej. Se cobró como SIN PRENDA y era CON PRENDA"
              value={hallazgoModal.nota}
              onChange={e => setHallazgoModal(h => ({ ...h, nota: e.target.value }))}
              style={{ ...filterInputStyle, marginTop: '6px', resize: 'vertical' }}
            />
            <div className="modal-actions" style={{ marginTop: '16px', display: 'flex', gap: '8px', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              {hallazgoModal.registro.hallazgo && (
                <button type="button" onClick={() => guardarHallazgo(false)} disabled={savingId !== null}
                  style={{ padding: '10px 16px', background: '#64748b', color: 'white', border: 'none', borderRadius: '10px', fontWeight: 600, cursor: 'pointer', marginRight: 'auto' }}>
                  Quitar hallazgo
                </button>
              )}
              <button type="button" className="btn-secondary" onClick={() => setHallazgoModal(null)}>Cancelar</button>
              <button type="button" onClick={() => guardarHallazgo(true)} disabled={savingId !== null || !hallazgoModal.nota.trim()}
                style={{ padding: '10px 16px', background: '#dc2626', color: 'white', border: 'none', borderRadius: '10px', fontWeight: 600, cursor: 'pointer' }}>
                Guardar hallazgo
              </button>
            </div>
          </div>
        </div>
      )}

      {selectedTramiteId && (
        <DetalleTramiteModal
          isOpen={!!selectedTramiteId}
          tramiteId={selectedTramiteId}
          onClose={() => setSelectedTramiteId(null)}
          onUpdate={() => {}}
        />
      )}
    </div>
  );
};

export default CambiosTramites;
